/**
 * GA4 identity beacon — purchase resilience (docs/purchase-resilience-plan.md).
 * OPT-IN: loaded by blocks/GA4_dataLayer.liquid only when the block setting
 * `enable_identity_beacon` is checked.
 *
 * Aggressive ad blockers kill the Web Pixel sandbox AND client-side GA/GTM, so
 * the ORDERS_CREATE webhook loses the GA4 identity (client_id/session/consent).
 * This script is the resilient first-party carrier for that identity: it writes
 * the values into CART ATTRIBUTES via the same-origin `POST /cart/update.js` —
 * Shopify then copies them onto the order as `note_attributes`, where the
 * webhook reads them (identityFromNoteAttributes / consentFromNoteAttributes).
 * Same pattern used by Littledata / Elevar / Stape.
 *
 * Why an asset file and not inline Liquid: the theme extension has a 100 KB
 * TOTAL Liquid budget and the (byte-synced) dataLayer snippet already uses
 * ~94 KB of it. Assets have their own 10 MB budget and are served from the
 * store's own CDN (cdn.shopify.com), which ad blockers cannot block wholesale
 * without breaking every Shopify store. The cart write itself stays same-origin.
 *
 * Safety against clobbering (both directions):
 * - The cart NOTE is a separate field and is never sent.
 * - `/cart/update.js` merges `attributes` PER KEY: keys we don't send are left
 *   exactly as they are, so a merchant's own cart attributes are never touched.
 * - Our identity keys are app-prefixed (`_spggtm_*`) so no other app/theme
 *   script can collide with them. The consent keys keep the pre-existing
 *   `_marketing_consent`/`_analytics_consent` convention the webhook already
 *   reads (an external writer of those would derive the same values).
 *
 * Written attributes (leading "_" hides them from most themes' order summaries):
 *   _spggtm_cid              GA4 client_id (from `_ga`, version prefix stripped)
 *   _spggtm_sid / _spggtm_sn GA4 session id / number (`_ga_<container>`, GS1/GS2;
 *                            property pinnable via `window.SPGGTM_GA4_MID`, see gaSession)
 *   _spggtm_y                Shopify visitor id (`_shopify_y`, ~1-year cookie)
 *   _spggtm_oppref           OpenAI/ChatGPT Ads click id (`__oppref`, written by the
 *                            OpenAI pixel SDK from the landing `?oppref=`, 30 d) — raw,
 *                            byte-identical to what the App Pixel reads. Task-44.
 *   _spggtm_gcl_aw / _spggtm_gcl_gb  Google Ads Conversion Linker cookies (`_gcl_aw` →
 *                            gclid, `_gcl_gb` → gbraid; `GCL.<ts>.<id>`, 90 d) — raw; the
 *                            webhook parses them into the flat `gclid` / `gbraid`. Task-46.
 *   _spggtm_fbp / _spggtm_fbc        Meta pixel cookies (`_fbp` browser id, `_fbc` click id,
 *                            `fb.<d>.<ms>.<id>`, 90 d) — raw; the webhook validates them and
 *                            sends them unchanged as the flat `fbp` / `fbc`. Task-48.
 *   _spggtm_ttp / _spggtm_ttclid     TikTok pixel cookies (`_ttp` browser id, `ttclid` click
 *                            id) — raw; validated, sent unchanged as `ttp` / `ttclid`. Task-48.
 *   _marketing_consent / _analytics_consent  real Consent Mode signals
 *
 * Writes are DIFFERENTIAL: a localStorage signature (which includes the cart
 * token) suppresses repeat POSTs until a value changes OR a NEW cart appears
 * (post-purchase / cleared cart) — ~one write per GA session / consent change.
 *
 * Debug: run `localStorage.setItem('spggtm_debug','1')` in the console.
 */
(function () {
  "use strict";
  var LS_KEY = "spggtm_id_beacon_sig";

  function debugOn() {
    try { return localStorage.getItem("spggtm_debug") === "1"; } catch (e) { return false; }
  }
  function dbg() {
    if (debugOn() && window.console) console.log.apply(console, ["[spggtm beacon]"].concat([].slice.call(arguments)));
  }

  function cookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[1]) : "";
  }
  // Raw variant (no decodeURIComponent): opaque ids that must stay byte-identical
  // across carriers (the App Pixel reads the same cookie raw). Task-44.
  function cookieRaw(name) {
    var m = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]+)"));
    return m ? m[1] : "";
  }

  // `_ga` = "GA1.1.<cid>" → keep only the client id part.
  function gaClientId() {
    var v = cookie("_ga");
    if (!v) return "";
    var m = v.match(/^GA\d+\.\d+\.(.+)$/);
    return m ? m[1] : v;
  }

  // Multi-property stores set SEVERAL `_ga_<container>` cookies and the order
  // of document.cookie is unstable. The theme (or the embed block) may pin the
  // target property by setting `window.SPGGTM_GA4_MID = "G-XXXXXXXXXX"` BEFORE
  // this script loads; the session is then read ONLY from that property's
  // cookie — absent (or malformed id) → sid/sn are omitted rather than
  // borrowed from a wrong property, which could stamp the purchase with a
  // foreign session. Without the global: legacy behavior, first cookie wins.
  function gaSessionCookieValue() {
    var mid = "";
    try { mid = String(window.SPGGTM_GA4_MID || ""); } catch (e) { /* noop */ }
    var suffix = mid.replace(/^G-/i, "").toUpperCase();
    if (suffix) {
      if (!/^[A-Z0-9-]+$/.test(suffix)) return "";
      return cookie("_ga_" + suffix);
    }
    var m = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9-]+=([^;]+)/);
    return m ? decodeURIComponent(m[1]) : "";
  }

  // `_ga_<CONTAINER>`: GS1.1.<sid>.<sn>....  |  GS2.1.s<sid>$o<sn>$...
  function gaSession() {
    var v = gaSessionCookieValue();
    if (!v) return { sid: "", sn: "" };
    if (v.indexOf("GS2.") === 0) {
      var body = v.split(".").slice(2).join(".");
      return {
        sid: (body.match(/(?:^|\$)s(\d+)/) || [])[1] || "",
        sn: (body.match(/(?:^|\$)o(\d+)/) || [])[1] || "",
      };
    }
    var parts = v.split(".");
    return { sid: parts[2] || "", sn: parts[3] || "" };
  }

  // Tri-state consent from Shopify's Customer Privacy API: "yes"/"no" are
  // written, "" (not asked / undecided) is OMITTED so consent stays UNKNOWN.
  function consentAttrs() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      var c = cp && cp.currentVisitorConsent && cp.currentVisitorConsent();
      if (!c) return {};
      var out = {};
      if (c.marketing === "yes" || c.marketing === "no") out._marketing_consent = String(c.marketing === "yes");
      if (c.analytics === "yes" || c.analytics === "no") out._analytics_consent = String(c.analytics === "yes");
      return out;
    } catch (e) { return {}; }
  }

  function collect() {
    var attrs = {};
    var cid = gaClientId();
    if (cid) attrs._spggtm_cid = cid;
    var s = gaSession();
    if (s.sid) attrs._spggtm_sid = s.sid;
    if (s.sn) attrs._spggtm_sn = s.sn;
    var y = cookie("_shopify_y");
    if (y) attrs._spggtm_y = y;
    // OpenAI/ChatGPT Ads click id — only when the OpenAI pixel wrote it (task-44).
    var o = cookieRaw("__oppref");
    if (o) attrs._spggtm_oppref = o;
    // Google Ads click-id cookies — only when the Conversion Linker wrote them, i.e. after an
    // ad-click landing with ad_storage granted (task-46). Raw; parsed server-side.
    var ga = cookieRaw("_gcl_aw");
    if (ga) attrs._spggtm_gcl_aw = ga;
    var gb = cookieRaw("_gcl_gb");
    if (gb) attrs._spggtm_gcl_gb = gb;
    // Meta + TikTok pixel cookies — only when the merchant's pixels wrote them (task-48). Raw;
    // validated server-side and forwarded unchanged to the sGTM Meta CAPI / TikTok tags.
    var fbp = cookieRaw("_fbp");
    if (fbp) attrs._spggtm_fbp = fbp;
    var fbc = cookieRaw("_fbc");
    if (fbc) attrs._spggtm_fbc = fbc;
    var ttp = cookieRaw("_ttp");
    if (ttp) attrs._spggtm_ttp = ttp;
    var ttclid = cookieRaw("ttclid");
    if (ttclid) attrs._spggtm_ttclid = ttclid;
    var c = consentAttrs();
    for (var k in c) attrs[k] = c[k];
    return attrs;
  }

  // Signature includes the cart token: a NEW cart (post-purchase, cleared)
  // starts without our attributes even though the cookies are unchanged.
  function computeSig(attrs, cartToken) {
    return (cartToken || "") + "|" + Object.keys(attrs).sort().map(function (k) {
      return k + "=" + attrs[k];
    }).join("&");
  }

  var inflight = false;
  function send() {
    try {
      if (inflight) return;
      var attrs = collect();
      if (!Object.keys(attrs).length) return;
      var sig = computeSig(attrs, cookie("cart"));
      try { if (localStorage.getItem(LS_KEY) === sig) return; } catch (e) { /* storage blocked */ }
      inflight = true;
      // Same-origin cart call. Sends ONLY `attributes` (never `note`); Shopify
      // merges attributes per key. Creates the cart if none exists yet.
      fetch("/cart/update.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ attributes: attrs }),
      }).then(function (r) {
        inflight = false;
        if (!(r && r.ok)) { dbg("cart/update failed", r && r.status); return; }
        // Save the signature against the REAL cart token (response wins over
        // cookie: on a first-ever write the cart may have just been created).
        r.json().then(function (cart) {
          var token = (cart && cart.token) || cookie("cart") || "";
          try { localStorage.setItem(LS_KEY, computeSig(attrs, token)); } catch (e) { /* storage blocked */ }
          dbg("attributes written", attrs);
        }, function () {
          try { localStorage.setItem(LS_KEY, computeSig(attrs, cookie("cart"))); } catch (e) { /* storage blocked */ }
        });
      }, function () { inflight = false; });
    } catch (e) { inflight = false; }
  }

  // GA sets/refreshes its cookies asynchronously after load → retry a few times.
  function schedule() {
    send();
    setTimeout(send, 3000);
    setTimeout(send, 8000);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule);
  } else {
    schedule();
  }
  // Consent decision (Shopify dispatches this on the document) → re-send.
  document.addEventListener("visitorConsentCollected", function () { setTimeout(send, 250); });
  // bfcache restores / navigation back → values or the cart may have rolled over.
  window.addEventListener("pageshow", function () { setTimeout(send, 500); });
})();
